import {
	BASE_PRECISION_EXP,
	BigNum,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { disallowNegativeStringInput } from '@velocity-exchange/common';
import {
	capStringFractionDigits,
	formatText,
	PRESETS,
} from '@velocity-exchange/common/format';
import { useRef } from 'react';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

interface Params {
	priceInputValue: string;
	baseSizeInputValueBigNum: BigNum;
	setPriceInputValue: (value: string) => void;
	setBaseSizeInputValue: (value: string) => void;
	setQuoteSizeInputValue: (value: string) => void;
	setIsQuoteSizeFromQuoteInput: (value: boolean) => void;
	isMarketOrder: boolean;
	markPriceToUse: BigNum;
	maxBaseSizeString: string;
	sizeDigits: number;
	openPositionSizeBigNum: BigNum;
	setCurrentSettings: (settings: {
		showClosePositionConfirmation: boolean;
	}) => void;
	showClosePositionConfirmation: boolean;
}

export default function useClosePositionModalHandlers({
	priceInputValue,
	baseSizeInputValueBigNum,
	setPriceInputValue,
	setBaseSizeInputValue,
	setQuoteSizeInputValue,
	setIsQuoteSizeFromQuoteInput,
	isMarketOrder,
	markPriceToUse,
	maxBaseSizeString,
	sizeDigits,
	openPositionSizeBigNum,
	setCurrentSettings,
	showClosePositionConfirmation,
}: Params) {
	// A quote typed while the price was empty or zero, so no base could be derived.
	// Price edits derive the base from it until a base or priced quote edit.
	const retainedQuoteRef = useRef<string | null>(null);

	// Derives the base size from a quote amount at a given price.
	const applyQuoteSize = (quoteValue: string, price: BigNum) => {
		if (price.lteZero()) {
			setBaseSizeInputValue('');
			setQuoteSizeInputValue(quoteValue);
			return;
		}

		const quoteSize = parseInputBigNumOrZero(quoteValue, QUOTE_PRECISION_EXP);
		const newBaseSize = quoteSize
			.scale(QUOTE_PRECISION, price.val)
			.shiftTo(BASE_PRECISION_EXP);

		const isGreaterThanMaxSize = newBaseSize.gt(openPositionSizeBigNum.abs());

		if (isGreaterThanMaxSize) {
			setSizeInputValuesToMaxSize(price, true);
			return;
		}

		const newBaseSizeInputValue = capStringFractionDigits(
			formatText(newBaseSize, PRESETS.printShort),
			{ maxFractionDigits: sizeDigits }
		);
		setBaseSizeInputValue(newBaseSizeInputValue);
		setQuoteSizeInputValue(quoteValue);
	};

	/**
	 * Updates the price input state and the new corresponding quote size input
	 * state, or derives the base from a retained quote.
	 */
	const handlePriceInputChange = (value: string) => {
		const price = parseInputBigNumOrZero(value, PRICE_PRECISION_EXP);

		if (retainedQuoteRef.current !== null) {
			applyQuoteSize(retainedQuoteRef.current, price);
		} else {
			setQuoteSizeInputValue(
				price.scalarMul(baseSizeInputValueBigNum).toFixed(2)
			);
		}

		setPriceInputValue(value);
	};

	/**
	 * Helper function to set the base / quote inputs to max possible.
	 */
	const setSizeInputValuesToMaxSize = (
		price: BigNum,
		updateQuote?: boolean
	) => {
		const newBaseSizeInputValue = capStringFractionDigits(maxBaseSizeString, {
			maxFractionDigits: sizeDigits,
		});
		setBaseSizeInputValue(newBaseSizeInputValue);

		if (updateQuote) {
			// Don't fix to 2 decimals, as it may result in an inaccurate base size value.
			const newQuoteSizeInputValue = price
				.scalarMul(openPositionSizeBigNum.abs())
				.printShort();
			setQuoteSizeInputValue(newQuoteSizeInputValue);
		}
	};

	/**
	 * Used for both the size input and position slider.
	 * Note that we don't update the quote size manually in this case,
	 * instead it is auto-calculated by a separate hook that is shared by
	 * both `ClosePositionModal` and `TpSlOrderFormPopup`.
	 */
	const handleBaseSizeInputChange = (value: string) => {
		setIsQuoteSizeFromQuoteInput(false);
		retainedQuoteRef.current = null;

		const price = parseInputBigNumOrZero(priceInputValue, PRICE_PRECISION_EXP);
		const isGreaterThanMaxSize = parseInputBigNumOrZero(
			value,
			BASE_PRECISION_EXP
		).gt(openPositionSizeBigNum.abs());

		if (isGreaterThanMaxSize) {
			setSizeInputValuesToMaxSize(price);
			return;
		}

		const newBaseSizeInputValue = capStringFractionDigits(value, {
			maxFractionDigits: sizeDigits,
		});
		setBaseSizeInputValue(newBaseSizeInputValue);
	};

	/**
	 * Updates the quote size input state and the new corresponding base size input state.
	 */
	const handleQuoteSizeInputChange = (value: string) => {
		setIsQuoteSizeFromQuoteInput(true);

		const price = isMarketOrder
			? markPriceToUse
			: parseInputBigNumOrZero(priceInputValue, PRICE_PRECISION_EXP);
		retainedQuoteRef.current = price.lteZero() ? value : null;

		applyQuoteSize(value, price);
	};

	const handleSizeInputChange = (
		value: string,
		inputSource: 'base' | 'quote'
	) => {
		const sanitizedValue = disallowNegativeStringInput(value);

		const updateFn =
			inputSource === 'base'
				? handleBaseSizeInputChange
				: handleQuoteSizeInputChange;

		updateFn(sanitizedValue);
	};

	const handlePositionSliderChange = (value: number) => {
		handleSizeInputChange(
			openPositionSizeBigNum.scale(value * 100, 10000).printShort(true),
			'base'
		);
	};

	const handleToggleShowClosePositionConfirmation = () => {
		setCurrentSettings({
			showClosePositionConfirmation: !showClosePositionConfirmation,
		});
	};

	return {
		handlePriceInputChange,
		handleSizeInputChange,
		handlePositionSliderChange,
		handleToggleShowClosePositionConfirmation,
	};
}
